// web/src/features/team/player/hooks/usePlayerSearch.ts
'use memo'

import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import { usesTrueGlobalPlayerSearch } from '@sportninja/common/utils/customer-name'
import {
  searchAdminPlayers,
  type AdminPlayerSearchResult,
} from '@sportninja/common/services/player/searchPlayers'

/** Minimum characters before a search fires (matches the legacy admin search). */
export const PLAYER_SEARCH_MIN_CHARS = 3

interface UsePlayerSearchArgs {
  /** The (already debounced) search text. */
  query: string
  organizationId?: string
  accountId?: string
  isGlobal?: boolean
}

/**
 * Resolve the scope params for /search/admin/players from the SN-7578 policy:
 * - true-global tenants (Canlan) with the toggle ON → no scope (truly global),
 * - other tenants with the toggle ON + a resolvable accountId → broaden to the
 *   account (org + account scope),
 * - otherwise → org-scoped (default, and the fallback when accountId is absent).
 */
const resolveScope = (
  organizationId: string | undefined,
  accountId: string | undefined,
  isGlobal: boolean
): { organizationId?: string; accountId?: string } => {
  if (isGlobal && usesTrueGlobalPlayerSearch) {
    return {}
  }
  if (isGlobal && !usesTrueGlobalPlayerSearch && accountId) {
    return { organizationId, accountId }
  }
  return { organizationId }
}

/** Whether the Global Search toggle should be shown at all (SN-7578). */
export const canShowGlobalSearchToggle = (accountId?: string): boolean =>
  usesTrueGlobalPlayerSearch || Boolean(accountId)

/** Label for the toggle: true-global vs account-broaden (SN-7578). */
export const globalSearchToggleLabel = (): string =>
  usesTrueGlobalPlayerSearch ? 'Global Search' : 'Global Search' //All Account Players

/**
 * Admin player search for the roster "Add Player" box. The caller passes a
 * DEBOUNCED query; React Query keys on it (+ the resolved scope) so each settled
 * query caches independently. Disabled until the query reaches the min length.
 * `placeholderData: keepPreviousData` keeps prior results visible while the next
 * loads (no flicker). Scope follows the SN-7578 tenant policy (see resolveScope).
 */
export const usePlayerSearch = ({
  query,
  organizationId,
  accountId,
  isGlobal = false,
}: UsePlayerSearchArgs) => {
  const trimmed = query.trim()
  const enabled = trimmed.length >= PLAYER_SEARCH_MIN_CHARS
  const scope = resolveScope(organizationId, accountId, isGlobal)

  const { data, isFetching, error } = useQuery<AdminPlayerSearchResult[]>({
    queryKey: queryKeys.adminPlayerSearch.byQuery(
      scope.organizationId ?? null,
      trimmed,
      // Key on the resolved account scope so account vs org searches cache apart.
      scope.accountId ?? (isGlobal ? 'global' : null)
    ),
    queryFn: () =>
      searchAdminPlayers({
        query: trimmed,
        organizationId: scope.organizationId,
        accountId: scope.accountId,
      }),
    enabled,
    placeholderData: keepPreviousData,
  })

  return {
    results: enabled ? data ?? [] : [],
    isSearching: enabled && isFetching,
    error: error as Error | null,
  }
}
